import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Settings, Loader2, Save, Phone, Mail, MapPin, Globe, CreditCard, Shield, IndianRupee, Link2, Building2 } from 'lucide-react';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    customer_care_no: '', whatsapp_no: '', support_hours: '9:00 AM - 6:00 PM',
    support_email: '', company_address: '', about_company: '', app_link: '',
    payment_gateway_key: '', min_retailer_amount: '100', max_retailer_amount: '50000',
    min_dist_amount: '1000', max_dist_amount: '500000', logo_url: '', user_logo_url: '',
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try { const r = await settingsAPI.getAdminSettings(); setSettings(prev => ({ ...prev, ...r.data })); } catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await settingsAPI.updateAdminSettings(settings); toast.success('Settings saved'); } catch (e) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const Field = ({ label, icon: Icon, field, placeholder, type = 'text' }) => (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground" />}{label}</Label>
      <Input type={type} placeholder={placeholder} value={settings[field]} onChange={e => setSettings({...settings, [field]: e.target.value})} data-testid={`admin-${field}`} />
    </div>
  );

  if (loading) return <DashboardLayout><div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="admin-settings-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Settings className="w-6 h-6" style={{ color: '#4680ff' }} />Admin Settings</h1>
            <p className="text-muted-foreground">Configure platform settings and branding</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="save-admin-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save All
          </Button>
        </div>

        <Tabs defaultValue="support">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="support" data-testid="tab-support"><Phone className="w-4 h-4 mr-2" />Support & Contact</TabsTrigger>
            <TabsTrigger value="payment" data-testid="tab-payment"><CreditCard className="w-4 h-4 mr-2" />Payment & Limits</TabsTrigger>
            <TabsTrigger value="branding" data-testid="tab-branding"><Globe className="w-4 h-4 mr-2" />Branding</TabsTrigger>
          </TabsList>

          <TabsContent value="support" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Support & Contact Info</CardTitle>
                <CardDescription>Customer care and support details shown in the app</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Customer Care No." icon={Phone} field="customer_care_no" placeholder="+91 9999999999" />
                  <Field label="WhatsApp No." icon={Phone} field="whatsapp_no" placeholder="+91 9999999999" />
                  <Field label="Support Hours" icon={Shield} field="support_hours" placeholder="9 AM - 6 PM" />
                  <Field label="Support Email" icon={Mail} field="support_email" placeholder="support@example.com" />
                </div>
                <Field label="Company Address" icon={MapPin} field="company_address" placeholder="Full address" />
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-muted-foreground" />About Company</Label>
                  <textarea rows={4} placeholder="Tell about your company..." value={settings.about_company} onChange={e => setSettings({...settings, about_company: e.target.value})} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="admin-about_company" />
                </div>
                <Field label="App Download Link" icon={Link2} field="app_link" placeholder="https://play.google.com/..." />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payment" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Gateway & Limits</CardTitle>
                <CardDescription>Configure payment settings and transaction limits</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <Field label="Payment Gateway Key" icon={CreditCard} field="payment_gateway_key" placeholder="Enter gateway key" />
                <div>
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><IndianRupee className="w-4 h-4" />Retailer Limits</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Min Amount" field="min_retailer_amount" placeholder="100" type="number" />
                    <Field label="Max Amount" field="max_retailer_amount" placeholder="50000" type="number" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><IndianRupee className="w-4 h-4" />Distributor Limits</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Min Amount" field="min_dist_amount" placeholder="1000" type="number" />
                    <Field label="Max Amount" field="max_dist_amount" placeholder="500000" type="number" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="branding" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Branding & Logos</CardTitle>
                <CardDescription>Upload logos and configure branding</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Home Logo URL" icon={Globe} field="logo_url" placeholder="https://example.com/logo.png" />
                {settings.logo_url && (
                  <div className="p-4 bg-muted rounded-lg"><p className="text-xs text-muted-foreground mb-2">Home Logo Preview:</p><img src={settings.logo_url} alt="Home Logo" className="h-12 object-contain" /></div>
                )}
                <Field label="User Panel Logo URL" icon={Globe} field="user_logo_url" placeholder="https://example.com/user-logo.png" />
                {settings.user_logo_url && (
                  <div className="p-4 bg-muted rounded-lg"><p className="text-xs text-muted-foreground mb-2">User Logo Preview:</p><img src={settings.user_logo_url} alt="User Logo" className="h-12 object-contain" /></div>
                )}
                <p className="text-sm text-muted-foreground">Upload your logo to any image hosting service and paste the URL here. Recommended size: 200x60px</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminSettings;
